import React, { useState, useEffect } from 'react';
import {
  Box,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Checkbox,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Visibility,
  Close,
  LocationOn,
  Phone,
  Email,
  CalendarToday,
  NavigateBefore,
  NavigateNext,
  Today,
  Refresh,
  LocalShipping,
  Person,
  CheckCircle,
  Map as MapIcon,
} from '@mui/icons-material';
import { format, addDays, startOfWeek, endOfWeek, parseISO, addWeeks } from 'date-fns';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';
import { OrderDetailsModal } from './UpcomingCollectionsModal';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons for different statuses
// Blue marker for pending/unassigned
const pendingIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjMjU2M2ViIj48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const assignedIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjMjU2M2ViIj48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const completedIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjMTBiOTgxIj48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

// Helper to create a coloured pin icon dynamically from any hex colour
const createColoredIcon = (color: string): L.Icon => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="${color}"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
  return new L.Icon({
    iconUrl: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
};

// Helper function to get the icon based on status and driver (uses live driver list)
const getMarkerIcon = (event: CollectionEvent, driverList: any[]): L.Icon => {
  if (event.status === 'completed') return completedIcon;
  if (event.status === 'pending' || !event.driver_name) return pendingIcon;

  // Look up driver colour from the fetched drivers list
  const driverNameLower = (event.driver_name || event.current_driver_name || '').toLowerCase();
  const driver = driverList.find((d: any) => d.name.toLowerCase() === driverNameLower);
  if (driver && driver.color) {
    return createColoredIcon(driver.color);
  }

  // Default to blue for other assigned drivers
  return assignedIcon;
};

interface CollectionEvent {
  id: number;
  order_id: number;
  woo_order_id: number;
  order_number: string;
  scheduled_date: string;
  status: 'pending' | 'assigned' | 'completed' | 'cancelled';
  assigned_driver_id?: number;
  driver_name?: string;
  current_driver_name?: string;
  driver_code?: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_email?: string;
  loyalty_member?: number;
  previous_orders_count?: number;
  customer_phone?: string;
  delivery_address_1: string;
  delivery_postcode: string;
  delivery_latitude?: number;
  delivery_longitude?: number;
  service_type?: string;
  product_names?: string;
  bins_total_services?: number;
  bags_total_services?: number;
  large_bins_total_services?: number;
  beds_count?: number;
  mattress_count?: number;
  total?: string;
  notes?: string;
  completed_at?: string;
  completed_by?: number;
  created_at?: string;
  updated_at?: string;
  customer_note?: string;
  items?: string;
  bin_colours?: string;
  bin_brown?: number;
  bin_green?: number;
  bin_blue?: number;
  bin_black?: number;
  bin_grey?: number;
  bin_pink?: number;
}

interface Driver {
  id: number;
  name: string;
  email: string;
  driver_code: string;
  status: string;
}

interface Order {
  id: number;
  woo_order_id: number;
  order_number: string;
  status: string;
  collection_status: string;
  total: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_email?: string;
  customer_phone?: string;
  delivery_postcode: string;
  shipping_collection_date: string;
  collection_dates?: string;
  service_type?: string;
  product_names?: string;
  driver_name?: string;
  assigned_user_id?: number;
  assigned_driver_id?: number;
  // Additional fields for detailed view
  delivery_address_1?: string;
  delivery_address_2?: string;
  delivery_city?: string;
  delivery_country?: string;
  billing_address_1?: string;
  billing_address_2?: string;
  billing_city?: string;
  billing_postcode?: string;
  billing_country?: string;
  date_created?: string;
  assigned_at?: string;
  completed_at?: string;
  customer_note?: string;
  bins_per_occasion?: number;
  bags_per_occasion?: number;
  bins_number_of_occasions?: number;
  bags_number_of_occasions?: number;
  bin_colours?: string;
  bin_brown?: number;
  bin_green?: number;
  bin_blue?: number;
  bin_black?: number;
  bin_grey?: number;
  bin_pink?: number;
  total_collections?: number;
  completed_collections?: number;
  pending_collections?: number;
}

// UK Bank Holidays for 2025 and 2026 (you can expand this list)
const bankHolidays = [
  '2025-01-01', // New Year's Day
  '2025-04-18', // Good Friday
  '2025-04-21', // Easter Monday
  '2025-05-05', // Early May bank holiday
  '2025-05-26', // Spring bank holiday
  '2025-08-25', // Summer bank holiday
  '2025-12-25', // Christmas Day
  '2025-12-26', // Boxing Day
  '2026-01-01', // New Year's Day
  '2026-04-03', // Good Friday
  '2026-04-06', // Easter Monday
  '2026-05-04', // Early May bank holiday
  '2026-05-25', // Spring bank holiday
  '2026-08-31', // Summer bank holiday
  '2026-12-25', // Christmas Day
  '2026-12-28', // Boxing Day (substitute)
];

// Helper function to get the next working day based on configured allowed_days
// allowed_days keys: "1"=Mon, "2"=Tue, "3"=Wed, "4"=Thu, "5"=Fri, "6"=Sat, "7"=Sun
// JS getDay(): 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
const jsGetDayToDbKey = (jsDay: number): string => jsDay === 0 ? '7' : String(jsDay);

const getNextWorkingDay = (allowedDays: { [key: string]: boolean }): Date => {
  let nextDay = addDays(new Date(), 1);

  while (true) {
    const dateString = format(nextDay, 'yyyy-MM-dd');
    const dbKey = jsGetDayToDbKey(nextDay.getDay());
    const isAllowedDay = allowedDays[dbKey] === true;
    const isBankHoliday = bankHolidays.includes(dateString);

    if (isAllowedDay && !isBankHoliday) {
      return nextDay;
    }
    nextDay = addDays(nextDay, 1);
  }
};

// Helper to format next working day label
const getNextWorkingDayLabel = (allowedDays: { [key: string]: boolean }): string => {
  const nextWorkDay = getNextWorkingDay(allowedDays);
  return format(nextWorkDay, 'EEE, MMM d');
};

const UpcomingCollections: React.FC = () => {
  const [events, setEvents] = useState<CollectionEvent[]>([]);
  const [allFilteredEvents, setAllFilteredEvents] = useState<CollectionEvent[]>([]); // For map display
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedEvents, setSelectedEvents] = useState<number[]>([]);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [orderDetailsModal, setOrderDetailsModal] = useState<{ open: boolean; order: Order | null }>({ open: false, order: null });
  const [orderCollectionEvents, setOrderCollectionEvents] = useState<CollectionEvent[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [dateRange, setDateRange] = useState({ start: new Date(), end: addDays(new Date(), 30) });
  const [filterDate, setFilterDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [timeRangeOption, setTimeRangeOption] = useState<'next-working-day' | 'specific-date' | number>('next-working-day'); // Default to next working day
  const [allowedDays, setAllowedDays] = useState<{ [key: string]: boolean }>({ "1": true, "2": true, "3": true, "4": true, "5": true, "6": false, "7": false });
  const [stats, setStats] = useState({
    total: 0,
    assigned: 0,
    unassigned: 0,
    completed: 0,
  });

  useEffect(() => {
    api.get('/collection-days/day-restrictions').then(res => {
      if (res.data.allowed_days) setAllowedDays(res.data.allowed_days);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchDrivers();
  }, [dateRange, filterDate, statusFilter, searchTerm, page, rowsPerPage, timeRangeOption, allowedDays]);

  const fetchEvents = async () => {
    try {
      setLoading(true);

      // Calculate days parameter based on timeRangeOption
      // For next-working-day or specific-date, we fetch enough days to ensure we get the data
      let days = 7;
      if (timeRangeOption === 'next-working-day' || timeRangeOption === 'specific-date') {
        days = 90; // Fetch 3 months to cover any specific date selection
      } else {
        days = (timeRangeOption as number) * 7;
      }

      // Fetch upcoming collection events
      const response = await api.get('/collection-events/upcoming', {
        params: {
          days: days,
          status: statusFilter || undefined,
          search: searchTerm || undefined
        }
      });

      console.log('API Response:', response.data);
      let allEvents = response.data.events || [];
      console.log('Events from API:', allEvents.length, 'events');

      // Filter out weekends and bank holidays
      allEvents = allEvents.filter((event: CollectionEvent) => {
        try {
          if (!event.scheduled_date) {
            console.log('No scheduled date for event:', event);
            return false;
          }

          // Handle both ISO format and date-only format
          let date: Date;
          if (event.scheduled_date.includes('T')) {
            // Already has time, use as-is
            date = new Date(event.scheduled_date);
          } else {
            // Date only, add time to prevent timezone issues
            date = new Date(event.scheduled_date + 'T12:00:00');
          }

          if (isNaN(date.getTime())) {
            console.error('Invalid date:', event.scheduled_date);
            return false;
          }

          // Extract just the date part for bank holiday check
          const dateString = event.scheduled_date.split('T')[0];
          const dbKey = jsGetDayToDbKey(date.getDay());
          const isAllowedDay = allowedDays[dbKey] === true;
          const isBankHoliday = bankHolidays.includes(dateString);

          return isAllowedDay && !isBankHoliday;
        } catch (error) {
          console.error('Date filter error:', error, event.scheduled_date);
          return false;
        }
      });

      // Apply "Next Working Day" filter if selected
      if (timeRangeOption === 'next-working-day') {
        const nextWorkDay = getNextWorkingDay(allowedDays);
        const nextWorkDayString = format(nextWorkDay, 'yyyy-MM-dd');
        allEvents = allEvents.filter((event: CollectionEvent) => {
          if (!event.scheduled_date) return false;
          const eventDate = event.scheduled_date.split('T')[0];
          return eventDate === nextWorkDayString;
        });
      }

      // Apply date filter if specified (overrides time range)
      if (filterDate) {
        allEvents = allEvents.filter((event: CollectionEvent) => {
          if (!event.scheduled_date) return false;
          // Compare just the date part
          const eventDate = event.scheduled_date.split('T')[0];
          const filterDatePart = filterDate.split('T')[0];
          return eventDate === filterDatePart;
        });
      }

      // Store all filtered events for map display
      setAllFilteredEvents(allEvents);

      // Calculate stats from all filtered events
      const assigned = allEvents.filter((e: CollectionEvent) => e.status === 'assigned').length;
      const completed = allEvents.filter((e: CollectionEvent) => e.status === 'completed').length;
      const pending = allEvents.filter((e: CollectionEvent) => e.status === 'pending').length;

      setStats({
        total: allEvents.length,
        assigned: assigned,
        unassigned: pending,
        completed: completed,
      });

      // Apply pagination
      const startIndex = page * rowsPerPage;
      const paginatedEvents = allEvents.slice(startIndex, startIndex + rowsPerPage);

      console.log('After all filtering:', allEvents.length, 'events remaining');
      console.log('Paginated events:', paginatedEvents.length);

      setEvents(paginatedEvents);
      setTotalCount(allEvents.length);
    } catch (error) {
      console.error('Error fetching collection events:', error);
      showNotification('Failed to fetch upcoming collections', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await api.get('/users', {
        params: { role: 'driver' }
      });
      setDrivers(response.data || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTimeRangeChange = (value: 'next-working-day' | 'specific-date' | number) => {
    setTimeRangeOption(value);
    // Clear the specific date filter when changing time range (unless it's 'specific-date')
    if (value !== 'specific-date') {
      setFilterDate('');
    }
    if (value === 'next-working-day') {
      const nextWorkDay = getNextWorkingDay(allowedDays);
      setDateRange({
        start: nextWorkDay,
        end: nextWorkDay
      });
    } else if (value !== 'specific-date') {
      setDateRange({
        start: new Date(),
        end: addWeeks(new Date(), value as number)
      });
    }
  };

  // Handle specific date selection - sets time range to 'specific-date' mode
  const handleDateFilterChange = (dateValue: string) => {
    setFilterDate(dateValue);
    if (dateValue) {
      setTimeRangeOption('specific-date');
    } else {
      // If date is cleared, go back to next working day
      setTimeRangeOption('next-working-day');
    }
  };

  const showNotification = (message: string, severity: 'success' | 'error') => {
    setNotification({ open: true, message, severity });
  };

  const handleEventStatusChange = async (eventId: number, newStatus: string) => {
    try {
      await api.patch(`/collection-events/${eventId}/status`, { status: newStatus });
      fetchEvents();
      showNotification('Collection status updated successfully', 'success');
    } catch (error) {
      console.error('Error updating collection status:', error);
      showNotification('Failed to update collection status', 'error');
    }
  };

  const handleDriverAssignment = async (eventId: number, driverId: number | '') => {
    try {
      if (driverId === '') {
        await api.patch(`/collection-events/${eventId}/assign`, { driverId: null });
      } else {
        await api.patch(`/collection-events/${eventId}/assign`, { driverId });
      }
      fetchEvents();
      showNotification('Driver assignment updated successfully', 'success');
    } catch (error) {
      console.error('Error updating driver assignment:', error);
      showNotification('Failed to update driver assignment', 'error');
    }
  };

  const handleViewDetails = async (event: CollectionEvent) => {
    try {
      setLoadingDetails(true);
      // Fetch full order details and all collection events for this order
      const [orderResponse, eventsResponse] = await Promise.all([
        api.get(`/orders/${event.order_id}`),
        api.get(`/collection-events/order/${event.order_id}`)
      ]);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setOrderCollectionEvents(eventsResponse.data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      showNotification('Failed to fetch order details', 'error');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsModal({ open: false, order: null });
    setOrderCollectionEvents([]);
  };

  const handleOverrideComplete = async (eventId: number) => {
    try {
      await api.put(`/collection-events/${eventId}/override-complete`);
      showNotification('Collection marked as completed', 'success');
      // Refresh the collection events and order details
      if (orderDetailsModal.order) {
        const [orderResponse, eventsResponse] = await Promise.all([
          api.get(`/orders/${orderDetailsModal.order.id}`),
          api.get(`/collection-events/order/${orderDetailsModal.order.id}`)
        ]);
        setOrderDetailsModal({ open: true, order: orderResponse.data });
        setOrderCollectionEvents(eventsResponse.data);
        // Refresh the main events list
        fetchEvents();
      }
    } catch (error) {
      console.error('Error marking collection as complete:', error);
      showNotification('Failed to mark collection as complete', 'error');
    }
  };

  const handleOverrideOrderComplete = async () => {
    if (!orderDetailsModal.order) return;

    try {
      await api.patch(`/orders/${orderDetailsModal.order.id}/override-complete`);
      showNotification('Order marked as completed', 'success');
      // Refresh the order details and collection events
      const [orderResponse, eventsResponse] = await Promise.all([
        api.get(`/orders/${orderDetailsModal.order.id}`),
        api.get(`/collection-events/order/${orderDetailsModal.order.id}`)
      ]);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setOrderCollectionEvents(eventsResponse.data);
      // Refresh the main events list
      fetchEvents();
    } catch (error) {
      console.error('Error marking order as complete:', error);
      showNotification('Failed to mark order as complete', 'error');
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const newSelected = events.map(ev => ev.id);
      setSelectedEvents(newSelected);
    } else {
      setSelectedEvents([]);
    }
  };

  const handleSelectOne = (id: number) => {
    const selectedIndex = selectedEvents.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedEvents, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedEvents.slice(1));
    } else if (selectedIndex === selectedEvents.length - 1) {
      newSelected = newSelected.concat(selectedEvents.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedEvents.slice(0, selectedIndex),
        selectedEvents.slice(selectedIndex + 1),
      );
    }

    setSelectedEvents(newSelected);
  };

  const getEventStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'assigned':
        return 'info';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getServiceTypeLabel = (event: CollectionEvent) => {
    const parts = [];

    // Calculate total bin count from individual colors
    const totalBins = (event.bin_brown || 0) +
                      (event.bin_green || 0) +
                      (event.bin_blue || 0) +
                      (event.bin_black || 0) +
                      (event.bin_grey || 0) +
                      (event.bin_pink || 0);

    // Show the actual counts
    if (totalBins > 0) {
      parts.push(`${totalBins} ${totalBins === 1 ? 'Bin' : 'Bins'}`);
    } else if (event.bins_total_services) {
      // Fallback to bins_total_services if no color breakdown
      parts.push(`${event.bins_total_services} ${event.bins_total_services === 1 ? 'Bin' : 'Bins'}`);
    }

    if (event.bags_total_services) {
      parts.push(`${event.bags_total_services} ${event.bags_total_services === 1 ? 'Bag' : 'Bags'}`);
    }

    if (event.large_bins_total_services) {
      parts.push(`${event.large_bins_total_services} Large ${event.large_bins_total_services === 1 ? 'Bin' : 'Bins'}`);
    }

    if (event.beds_count) {
      parts.push(`${event.beds_count} ${event.beds_count === 1 ? 'Bed' : 'Beds'}`);
    }

    if (event.mattress_count) {
      parts.push(`${event.mattress_count} ${event.mattress_count === 1 ? 'Mattress' : 'Mattresses'}`);
    }

    return parts.join(', ') || event.service_type || 'Standard';
  };

  const getBinColours = (item: CollectionEvent | Order): string[] => {
    const colours: string[] = [];

    // Check individual bin colour counts
    if (item.bin_brown && item.bin_brown > 0) colours.push(`${item.bin_brown} Brown`);
    if (item.bin_green && item.bin_green > 0) colours.push(`${item.bin_green} Green`);
    if (item.bin_blue && item.bin_blue > 0) colours.push(`${item.bin_blue} Blue`);
    if (item.bin_black && item.bin_black > 0) colours.push(`${item.bin_black} Black`);
    if (item.bin_grey && item.bin_grey > 0) colours.push(`${item.bin_grey} Grey`);
    if (item.bin_pink && item.bin_pink > 0) colours.push(`${item.bin_pink} Pink`);

    // If no individual counts but bin_colours field exists, try to parse it
    if (colours.length === 0 && item.bin_colours) {
      try {
        const parsedColours = JSON.parse(item.bin_colours);
        if (Array.isArray(parsedColours)) {
          return parsedColours;
        }
      } catch (e) {
        // If not JSON, return as is
        return [item.bin_colours];
      }
    }

    return colours;
  };

  // Get events with valid coordinates for map display
  const eventsWithCoordinates = allFilteredEvents.filter(
    event => event.delivery_latitude && event.delivery_longitude
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Upcoming Collections
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={() => {
            setFilterDate('');
            setStatusFilter('');
            setSearchTerm('');
            setTimeRangeOption('next-working-day');
            fetchEvents();
          }}
          variant="contained"
          size="small"
          sx={{ backgroundColor: '#23a244', '&:hover': { backgroundColor: '#1e8e3a' } }}
        >
          Refresh
        </Button>
      </Box>

      {/* Top Row: Map on left, Stats on right */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Map - takes up 8 columns */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '400px' }}>
            <Typography variant="h6" gutterBottom>
              Upcoming Collections Map {
                timeRangeOption === 'specific-date' && filterDate
                  ? `(${format(new Date(filterDate + 'T12:00:00'), 'EEE, MMM d')})`
                  : timeRangeOption === 'next-working-day'
                    ? `(${getNextWorkingDayLabel(allowedDays)})`
                    : `(Next ${timeRangeOption} ${timeRangeOption === 1 ? 'Week' : 'Weeks'})`
              }
            </Typography>
            <Box sx={{ height: 'calc(100% - 40px)', width: '100%', position: 'relative' }}>
              <MapContainer
                center={[53.4808, -2.2426]}
                zoom={11}
                style={{ height: '100%', width: '100%', borderRadius: '4px' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                {/* Collection markers */}
                {eventsWithCoordinates.map((event) => {
                  const icon = getMarkerIcon(event, drivers);

                  return (
                    <Marker
                      key={`collection-${event.id}`}
                      position={[
                        parseFloat(event.delivery_latitude!.toString()),
                        parseFloat(event.delivery_longitude!.toString())
                      ]}
                      icon={icon}
                    >
                      <Popup>
                        <div style={{ minWidth: '200px' }}>
                          <strong>Order #{event.order_number}</strong><br />
                          <strong>{event.customer_first_name} {event.customer_last_name}</strong><br />
                          {event.delivery_address_1}<br />
                          {event.delivery_postcode}<br />
                          {event.customer_phone && <><br />Tel: {event.customer_phone}</>}
                          <br /><br />
                          <strong>Date:</strong> {(() => {
                            try {
                              const dateString = event.scheduled_date.split('T')[0];
                              const [year, month, day] = dateString.split('-');
                              const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
                              return format(date, 'EEE, MMM d');
                            } catch {
                              return event.scheduled_date;
                            }
                          })()}<br />
                          <strong>Service:</strong> {getServiceTypeLabel(event)}<br />
                          <strong>Driver:</strong> {event.driver_name || event.current_driver_name || 'Unassigned'}<br />
                          <strong>Status:</strong> <span style={{ textTransform: 'capitalize' }}>{event.status}</span>
                          <br /><br />
                          <FormControl size="small" fullWidth>
                            <Select
                              value={event.assigned_driver_id || ''}
                              onChange={(e) => handleDriverAssignment(event.id, e.target.value as number | '')}
                              displayEmpty
                              disabled={event.status === 'completed'}
                              sx={{ fontSize: '12px' }}
                            >
                              <MenuItem value=""><em>Unassigned</em></MenuItem>
                              {drivers
                                .filter(driver => driver.email !== 'system@historical.local' && driver.status === 'active')
                                .map((driver) => (
                                  <MenuItem key={driver.id} value={driver.id}>
                                    {driver.name} {driver.driver_code ? `(${driver.driver_code})` : ''}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
              {/* Map Legend */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  right: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: 1,
                  p: 1,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  zIndex: 1000,
                  fontSize: '11px',
                }}
              >
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                  Driver Key
                </Typography>
                {drivers
                  .filter((d: any) => d.email !== 'system@historical.local' && d.status === 'active')
                  .map((driver: any) => (
                    <Box key={driver.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.3, gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, backgroundColor: driver.color || '#2563EB', borderRadius: '50%' }} />
                      <span>{driver.name}</span>
                    </Box>
                  ))}
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mt: 0.5, mb: 0.3 }}>
                  Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#2563eb', borderRadius: '50%', mr: 0.5 }} />
                  <span>Pending</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#10b981', borderRadius: '50%', mr: 0.5 }} />
                  <span>Completed</span>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Stats Cards - takes up 4 columns */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Card sx={{ height: '190px' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <CalendarToday color="primary" sx={{ fontSize: 35, mb: 1 }} />
                  <Typography variant="h3">
                    {stats.total}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    Total
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ height: '190px' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Person color="info" sx={{ fontSize: 35, mb: 1 }} />
                  <Typography variant="h3">
                    {stats.assigned}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    Assigned
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ height: '190px' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <LocalShipping color="warning" sx={{ fontSize: 35, mb: 1 }} />
                  <Typography variant="h3">
                    {stats.unassigned}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    Pending
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Filters and Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Order #, Customer, Postcode"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Specific Date"
              variant="outlined"
              size="small"
              value={filterDate}
              onChange={(e) => handleDateFilterChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRangeOption}
                onChange={(e) => handleTimeRangeChange(e.target.value as 'next-working-day' | 'specific-date' | number)}
                label="Time Range"
              >
                <MenuItem value="next-working-day">Next Working Day ({getNextWorkingDayLabel(allowedDays)})</MenuItem>
                {timeRangeOption === 'specific-date' && (
                  <MenuItem value="specific-date">
                    Specific Date ({filterDate ? format(new Date(filterDate + 'T12:00:00'), 'EEE, MMM d') : ''})
                  </MenuItem>
                )}
                <MenuItem value={1}>Next Week</MenuItem>
                <MenuItem value={2}>Next 2 Weeks</MenuItem>
                <MenuItem value={4}>Next 4 Weeks</MenuItem>
                <MenuItem value={8}>Next 8 Weeks</MenuItem>
                <MenuItem value={12}>Next 3 Months</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setFilterDate('');
                setStatusFilter('');
                setSearchTerm('');
                setTimeRangeOption('next-working-day');
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Collections Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedEvents.length > 0 && selectedEvents.length < events.length}
                  checked={events.length > 0 && selectedEvents.length === events.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Collection Date</TableCell>
              <TableCell>Order #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell sx={{ maxWidth: 200 }}>Address</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Driver</TableCell>
              <TableCell>Collect</TableCell>
              <TableCell>Bin Colours</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => (
              <TableRow
                key={event.id}
                hover
                selected={selectedEvents.indexOf(event.id) !== -1}
                sx={{
                  bgcolor: event.status === 'completed' ? 'action.hover' : 'inherit',
                  opacity: event.status === 'completed' ? 0.7 : 1
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedEvents.indexOf(event.id) !== -1}
                    onChange={() => handleSelectOne(event.id)}
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {(() => {
                        try {
                          if (!event.scheduled_date) return 'No date';

                          // Extract just the date part if it's in ISO format
                          const dateString = event.scheduled_date.split('T')[0];

                          // Parse the date string properly for display
                          const [year, month, day] = dateString.split('-');
                          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);

                          if (isNaN(date.getTime())) {
                            return dateString;
                          }
                          return format(date, 'EEE, MMM d');
                        } catch (error) {
                          console.error('Date format error:', error, event.scheduled_date);
                          return event.scheduled_date || 'Invalid date';
                        }
                      })()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(() => {
                        try {
                          if (!event.scheduled_date) return '';

                          // Extract just the date part if it's in ISO format
                          const dateString = event.scheduled_date.split('T')[0];

                          // Parse the date string properly for display
                          const [year] = dateString.split('-');
                          return year;
                        } catch (error) {
                          return '';
                        }
                      })()}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{event.order_number}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {event.customer_first_name} {event.customer_last_name}
                    {(event.loyalty_member ?? 0) > 0 && (event.previous_orders_count ?? 0) > 0 && (
                      <Tooltip title="These are customers with accounts and are part of the loyalty scheme">
                        <Box
                          component="span"
                          sx={{ ml: 0.5, color: 'primary.main', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1, cursor: 'help' }}
                        >
                          *
                        </Box>
                      </Tooltip>
                    )}
                  </Typography>
                  {event.customer_phone && (
                    <Typography variant="caption" color="text.secondary">
                      {event.customer_phone}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={event.delivery_address_1}
                  >
                    {event.delivery_address_1}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {event.delivery_postcode}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 180 }}>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={event.assigned_driver_id || ''}
                      onChange={(e) => handleDriverAssignment(event.id, e.target.value as number | '')}
                      displayEmpty
                      disabled={event.status === 'completed'}
                    >
                      <MenuItem value="">
                        <em>Unassigned</em>
                      </MenuItem>
                      {drivers
                        .filter(driver => driver.email !== 'system@historical.local' && driver.status === 'active')
                        .map((driver) => (
                          <MenuItem key={driver.id} value={driver.id}>
                            {driver.name} {driver.driver_code ? `(${driver.driver_code})` : ''}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {getServiceTypeLabel(event)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="primary">
                    {getBinColours(event).join(', ') || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={event.status.toUpperCase()}
                    color={getEventStatusColor(event.status)}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleViewDetails(event)}
                    title="View Details"
                  >
                    <Visibility />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {events.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No upcoming collections found (weekends and bank holidays are excluded)
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Order Details Modal */}
      <OrderDetailsModal
        open={orderDetailsModal.open}
        order={orderDetailsModal.order}
        collectionEvents={orderCollectionEvents}
        loadingDetails={loadingDetails}
        onClose={handleCloseOrderDetails}
        onOverrideComplete={handleOverrideComplete}
        onOverrideOrderComplete={handleOverrideOrderComplete}
        getBinColours={getBinColours}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UpcomingCollections;
